import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Api } from '../api/api';
import { AppText } from '../components/AppText';
import { InlineNotice } from '../components/InlineNotice';
import { PrimaryButton } from '../components/PrimaryButton';
import { PublicRoute } from '../navigation/routes';
import { Strings } from '../strings';

/**
 * The page a verification link opens.
 *
 * A **page**, not a screen, for the same reason as `ResetPasswordPage`: the three clients share a screen
 * inventory that `check-parity.sh` enforces, and this is not part of it. The apps open this page rather
 * than each growing their own, so it is a genuine web-only surface — divergence 18 in the parity register.
 *
 * Public, because the mail is often read on a different device from the one the parent signed in on.
 *
 * It confirms **on load** rather than behind a button. There is nothing to decide: the parent already
 * decided by clicking the link in their mail, and a second "are you sure" is a step that only creates a
 * way to fail.
 */
export function VerifyEmailPage() {
  const [parameters] = useSearchParams();
  const navigate = useNavigate();
  const token = parameters.get('token');

  const [error, setError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  // React runs effects twice in development. Confirming is single-use, so the second call would report the
  // link as already spent and show an error on a success.
  const attempted = useRef(false);

  useEffect(() => {
    if (token === null || attempted.current) return;

    attempted.current = true;

    void (async () => {
      const result = await Api.verifyEmail(token);

      if (result.ok) {
        setIsDone(true);

        return;
      }

      setError(result.error.message);
    })();
  }, [token]);

  return (
    <div
      style={{
        maxWidth: 'var(--column)',
        margin: '0 auto',
        padding: 'var(--space-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <AppText as="h1" style="titleL">
        {Strings.verifyEmailBanner}
      </AppText>

      {token === null || error !== null ? (
        <InlineNotice text={error ?? Strings.verificationLinkMissing} tone="info" />
      ) : isDone ? (
        <InlineNotice text={Strings.verifyEmailDone} tone="info" />
      ) : (
        <InlineNotice text={Strings.verifyEmailConfirming} tone="info" />
      )}

      <PrimaryButton label={Strings.actionHaveAccount} onClick={() => navigate(PublicRoute.signIn)} />
    </div>
  );
}
